/**
 * Switch — Interruptor binario accesible y reutilizable.
 *
 * QUÉ HACE:
 *  - Renderiza un toggle controlado (role="switch") con tres tamaños (sm/md/lg).
 *  - `SwitchRow` añade icono + label + descripción a la izquierda y el switch a la derecha,
 *    pensado para listas de preferencias (POS, notificaciones, voz, tema, etc.).
 *  - Soporta estado deshabilitado y descripción de bloqueo opcional (`lockReason`).
 *
 * QUÉ EXPORTA: Switch (named), SwitchRow (named), SwitchSize (type)
 *
 * QUÉ NO DEBE HACER:
 *  - No realiza llamadas a API ni lógica de negocio.
 *  - No persiste estado: es controlado (`checked` + `onChange`).
 *  - No define paletas locales: respeta el design system (primary / gray / slate).
 *
 * REGLAS:
 *  - Para opciones binarias en formularios y paneles, USAR este componente
 *    en lugar de inputs nativos `type="checkbox"` con estilos ad-hoc.
 *  - El padre decide cómo persistir (localStorage, store, etc.).
 *  - Para que sea reutilizable a través de roles, NO añadir aquí lógica condicional
 *    por permisos: eso debe ir en el contenedor.
 */
import { ReactNode, useId } from "react";

export type SwitchSize = "sm" | "md" | "lg";

interface SwitchProps {
  checked: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
  size?: SwitchSize;
  /** Texto accesible (sr-only) cuando no hay label visible alrededor. */
  ariaLabel?: string;
  /** ID para enlazar con un <label htmlFor>. */
  id?: string;
  className?: string;
}

const SWITCH_DIM: Record<
  SwitchSize,
  {
    track: string;
    thumb: string;
    translate: string;
  }
> = {
  sm: {
    track: "w-8 h-4",
    thumb: "w-3 h-3",
    translate: "translate-x-4",
  },
  md: {
    track: "w-10 h-5",
    thumb: "w-4 h-4",
    translate: "translate-x-5",
  },
  lg: {
    track: "w-12 h-6",
    thumb: "w-5 h-5",
    translate: "translate-x-6",
  },
};

export const Switch = ({
  checked,
  onChange,
  disabled = false,
  size = "md",
  ariaLabel,
  id,
  className = "",
}: SwitchProps) => {
  const dim = SWITCH_DIM[size];
  return (
    <button
      id={id}
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={ariaLabel}
      onClick={() => !disabled && onChange(!checked)}
      disabled={disabled}
      className={`relative inline-flex items-center rounded-full flex-shrink-0 transition-colors duration-200
        focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/40
        disabled:opacity-40 disabled:cursor-not-allowed
        ${dim.track}
        ${
          checked
            ? "bg-primary-600 dark:bg-primary-500"
            : "bg-gray-300 dark:bg-slate-600"
        }
        ${className}`}
    >
      <span
        className={`absolute top-0.5 left-0.5 rounded-full bg-white shadow-sm transition-transform duration-200
          ${dim.thumb}
          ${checked ? dim.translate : "translate-x-0"}`}
      />
    </button>
  );
};

interface SwitchRowProps {
  checked: boolean;
  onChange: (next: boolean) => void;
  label: string;
  description?: ReactNode;
  icon?: ReactNode;
  disabled?: boolean;
  /** Mensaje en lugar de la descripción cuando está bloqueado. */
  lockReason?: string;
  size?: SwitchSize;
  className?: string;
}

/**
 * Fila preformateada: icono + label + descripción (o lockReason si disabled).
 * Toda la fila es clickable. Pensada para paneles compactos en móvil.
 */
export const SwitchRow = ({
  checked,
  onChange,
  label,
  description,
  icon,
  disabled = false,
  lockReason,
  size = "md",
  className = "",
}: SwitchRowProps) => {
  const uid = useId();
  const switchId = `switch-${uid}`;
  const showLock = disabled && !!lockReason;
  return (
    <div
      className={`flex items-center gap-3 px-3 py-2.5 rounded-xl border transition-colors
        ${
          disabled
            ? "border-gray-100 dark:border-slate-700/60 bg-gray-50/60 dark:bg-slate-800/40"
            : "border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-900 hover:border-gray-300 dark:hover:border-slate-500"
        }
        ${className}`}
    >
      {icon ? (
        <span
          className={`shrink-0 w-8 h-8 rounded-lg flex items-center justify-center
            ${
              checked && !disabled
                ? "bg-primary-50 text-primary-600 dark:bg-primary-950/40 dark:text-primary-300"
                : "bg-gray-100 text-gray-500 dark:bg-slate-800 dark:text-slate-400"
            }`}
          aria-hidden
        >
          {icon}
        </span>
      ) : null}
      <button
        type="button"
        onClick={() => !disabled && onChange(!checked)}
        disabled={disabled}
        className="flex-1 text-left min-w-0 focus:outline-none disabled:cursor-not-allowed"
        aria-controls={switchId}
      >
        <label
          htmlFor={switchId}
          className={`block text-xs font-bold leading-tight cursor-pointer
            ${
              disabled
                ? "text-gray-500 dark:text-slate-400"
                : "text-gray-800 dark:text-slate-100"
            }`}
        >
          {label}
        </label>
        {showLock ? (
          <p className="mt-0.5 text-[10px] font-medium text-amber-600 dark:text-amber-400 leading-snug">
            {lockReason}
          </p>
        ) : description ? (
          <p className="mt-0.5 text-[10px] text-gray-500 dark:text-slate-400 leading-snug">
            {description}
          </p>
        ) : null}
      </button>
      <Switch
        id={switchId}
        checked={checked}
        onChange={onChange}
        disabled={disabled}
        size={size}
        ariaLabel={label}
      />
    </div>
  );
};
